export const dentalConversationChannels = ["Phone", "Email", "WhatsApp", "Web Chat"] as const;

export type DentalConversationChannel = (typeof dentalConversationChannels)[number];
export type DentalConversationFilter = "All channels" | DentalConversationChannel;
export type DentalConversationTone = "complete" | "review" | "handoff";

export type DentalEmailMetadata = {
  subject: string;
  from: string;
  to: string;
  time: string;
  dateTime: string;
  quotedMessage?: { from: string; time: string; text: string };
};

export type DentalConversationEvent =
  | { kind: "message"; speaker: "patient" | "assistant"; text: string; email?: DentalEmailMetadata }
  | { kind: "action"; title: string; details: string[]; tone: DentalConversationTone };

export type DentalConversationExample = {
  id: string;
  patient: string;
  initials: string;
  channel: DentalConversationChannel;
  subject: string;
  topic: string;
  preview: string;
  time: string;
  status: string;
  tone: DentalConversationTone;
  events: DentalConversationEvent[];
  outcome: { title: string; detail: string; owner: string; label: string; meta: string };
};

// Fictional landing-page examples, never connected to patient records or live actions.
export const dentalConversationExamples: DentalConversationExample[] = [
  {
    id: "new-patient",
    patient: "Emma Wilson",
    initials: "EW",
    channel: "Web Chat",
    subject: "A first visit, booked",
    topic: "New patient",
    preview: "A new patient finds a cleaning appointment after work.",
    time: "9:42 AM",
    status: "Booked",
    tone: "complete",
    events: [
      { kind: "message", speaker: "patient", text: "Hi! I'm a new patient. Can I book a dental cleaning after 4 PM?" },
      { kind: "message", speaker: "assistant", text: "Of course. Which day would suit you, and may I have your name?" },
      { kind: "message", speaker: "patient", text: "Thursday would be great. I'm Emma Wilson." },
      { kind: "action", title: "Available appointments checked", tone: "complete", details: ["Visit: new-patient dental cleaning", "Preference: Thursday, after 4 PM", "Matching opening: Thursday at 4:30 PM"] },
      { kind: "message", speaker: "assistant", text: "There's a Thursday opening at 4:30 PM. Would you like to book it?" },
      { kind: "message", speaker: "patient", text: "Yes, please. You can reach me at emma@example.com." },
      { kind: "action", title: "Appointment created", tone: "complete", details: ["Patient confirmed: Emma Wilson", "Contact: emma@example.com", "Appointment: dental cleaning · Thursday, 4:30 PM", "Scheduling action enabled in this example"] },
      { kind: "message", speaker: "assistant", text: "You're booked for Thursday at 4:30 PM, Emma. Your visit details are ready. We look forward to meeting you!" },
    ],
    outcome: { title: "From inquiry to a booked visit", detail: "Dental cleaning · Thursday, 4:30 PM. Patient details and the booking stay with the conversation.", owner: "Scheduling", label: "Cleaning booked", meta: "Thursday · 4:30 PM" },
  },
  {
    id: "reschedule",
    patient: "James Parker",
    initials: "JP",
    channel: "WhatsApp",
    subject: "A change of plans, handled",
    topic: "Reschedule",
    preview: "An existing patient moves a cleaning without a callback.",
    time: "9:28 AM",
    status: "Booked",
    tone: "complete",
    events: [
      { kind: "message", speaker: "patient", text: "Hi, I can't make my Tuesday cleaning. Could we move it to Friday?" },
      { kind: "message", speaker: "assistant", text: "Of course. May I have your full name and the best phone number or email to reach you?" },
      { kind: "message", speaker: "patient", text: "I'm James Parker. You can reach me at james@example.com." },
      { kind: "action", title: "Patient details captured", tone: "complete", details: ["Name: James Parker", "Email: james@example.com", "Request: move Tuesday's cleaning to Friday"] },
      { kind: "message", speaker: "assistant", text: "Thanks, James. Friday at 10 AM is available. Shall I move your Tuesday cleaning to that time?" },
      { kind: "message", speaker: "patient", text: "Yes, Friday at 10 AM works. Thank you!" },
      { kind: "action", title: "Appointment rescheduled", tone: "complete", details: ["Original visit: Tuesday dental cleaning", "Patient-approved replacement: Friday at 10 AM", "Original appointment updated; no duplicate created", "Rescheduling action enabled in this example"] },
      { kind: "message", speaker: "assistant", text: "All set — your cleaning is now on Friday at 10 AM. Your Tuesday appointment has been moved." },
    ],
    outcome: { title: "A new time. The same conversation.", detail: "Cleaning moved to Friday, 10 AM, with the patient's confirmation and change history attached.", owner: "Scheduling", label: "Cleaning rescheduled", meta: "Friday · 10:00 AM" },
  },
  {
    id: "insurance",
    patient: "Olivia Chen",
    initials: "OC",
    channel: "Email",
    subject: "An insurance inquiry, followed by a booking",
    topic: "Insurance",
    preview: "An insurance question leads to a new-patient checkup.",
    time: "9:15 AM",
    status: "Booked",
    tone: "complete",
    events: [
      {
        kind: "message", speaker: "patient", text: "I'm Olivia Chen and I have Bright Dental PPO. Could I book a new-patient checkup on Monday morning? You can reach me at olivia@example.com.",
        email: { subject: "Checkup booking & insurance", from: "Olivia Chen <olivia@example.com>", to: "Front desk <hello@practice.example>", time: "9:02 AM", dateTime: "2026-08-12T09:02:00" },
      },
      {
        kind: "message", speaker: "assistant", text: "Hi Olivia, Monday at 11 AM is available. I can note your plan for the billing team; coverage and costs still need to be checked. Shall I book that time?",
        email: { subject: "Re: Checkup booking & insurance", from: "Front desk <hello@practice.example>", to: "Olivia Chen <olivia@example.com>", time: "9:03 AM", dateTime: "2026-08-12T09:03:00" },
      },
      {
        kind: "message", speaker: "patient", text: "Yes, Monday at 11 AM works. Please book it and send the details to this email.",
        email: { subject: "Re: Checkup booking & insurance", from: "Olivia Chen <olivia@example.com>", to: "Front desk <hello@practice.example>", time: "9:12 AM", dateTime: "2026-08-12T09:12:00" },
      },
      { kind: "action", title: "Checkup appointment created", tone: "complete", details: ["Patient: Olivia Chen", "Email: olivia@example.com", "Patient-approved visit: new-patient checkup · Monday, 11 AM", "Plan noted: Bright Dental PPO (fictional); coverage and costs not yet verified"] },
      {
        kind: "message", speaker: "assistant", text: "You're booked for Monday at 11 AM, Olivia. I've noted Bright Dental PPO for the billing team to check before your visit. We look forward to meeting you!",
        email: {
          subject: "Re: Checkup booking & insurance", from: "Front desk <hello@practice.example>", to: "Olivia Chen <olivia@example.com>", time: "9:15 AM", dateTime: "2026-08-12T09:15:00",
          quotedMessage: { from: "Olivia Chen", time: "9:12 AM", text: "Yes, Monday at 11 AM works. Please book it and send the details to this email." },
        },
      },
    ],
    outcome: { title: "From an insurance inquiry to a booked checkup", detail: "New-patient checkup booked for Monday, 11 AM. Plan details stay with the appointment; no coverage promise is made.", owner: "Scheduling", label: "Checkup booked", meta: "Monday · 11:00 AM" },
  },
  {
    id: "consultation",
    patient: "Daniel Brooks",
    initials: "DB",
    channel: "Phone",
    subject: "An implant consultation, booked",
    topic: "Implant consult",
    preview: "A treatment inquiry becomes a booked consultation.",
    time: "8:56 AM",
    status: "Booked",
    tone: "complete",
    events: [
      { kind: "message", speaker: "patient", text: "I'd like to book a consultation to discuss dental implants." },
      { kind: "message", speaker: "assistant", text: "Of course. May I have your name and the best phone number or email to reach you?" },
      { kind: "message", speaker: "patient", text: "I'm Daniel Brooks. My email is daniel@example.com. A Wednesday afternoon would be ideal." },
      { kind: "action", title: "Patient details captured", tone: "complete", details: ["Name: Daniel Brooks", "Email: daniel@example.com", "Request: dental implant consultation", "Preference: Wednesday afternoon"] },
      { kind: "message", speaker: "assistant", text: "Thanks, Daniel. Wednesday at 2 PM is available for a consultation with the dentist. Would you like to book it?" },
      { kind: "message", speaker: "patient", text: "Yes, Wednesday at 2 PM works. Please book it." },
      { kind: "action", title: "Consultation appointment created", tone: "complete", details: ["Patient confirmed: Daniel Brooks", "Visit: dental implant consultation · Wednesday, 2 PM", "Contact: daniel@example.com", "Consultation only; treatment options will be discussed with the dentist"] },
      { kind: "message", speaker: "assistant", text: "You're booked for Wednesday at 2 PM, Daniel. Your consultation details are ready. We look forward to seeing you." },
    ],
    outcome: { title: "A treatment question. A consultation booked.", detail: "Dental implant consultation booked for Wednesday, 2 PM, with Daniel's contact details and request attached.", owner: "Scheduling", label: "Consultation booked", meta: "Wednesday · 2:00 PM" },
  },
];

export function filterDentalConversations(channel: DentalConversationFilter): DentalConversationExample[] {
  return dentalConversationExamples.filter((conversation) => channel === "All channels" || conversation.channel === channel);
}

export function selectDentalConversation(id: string, channel: DentalConversationFilter): DentalConversationExample {
  const conversations = filterDentalConversations(channel);
  return conversations.find((conversation) => conversation.id === id) ?? conversations[0];
}
